import React, { useMemo, useState } from "react";
import {
  Box, Chip, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, alpha, IconButton, Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PaidIcon from "@mui/icons-material/Paid";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useGetBillingReportQuery } from "../../../Api/attoDeskApi";
import { IBillingRow } from "../../../Api/Interface/api.interface";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import {
  NAVY, GOLD, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

const formatMoney = (n: number): string =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  trial:     { bg: "#dbeafe", fg: "#1e40af" },
  active:    { bg: "#dcfce7", fg: "#166534" },
  past_due:  { bg: "#fef3c7", fg: "#92400e" },
  suspended: { bg: "#fee2e2", fg: "#991b1b" },
  cancelled: { bg: "#f1f5f9", fg: "#475569" },
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.cancelled;
  return (
    <Chip size="small" label={status.toUpperCase()}
      sx={{
        height: 22, fontSize: 11, fontWeight: 700, letterSpacing: "0.02em",
        bgcolor: s.bg, color: s.fg,
      }} />
  );
};

const BillingReport: React.FC = () => {
  const navigate = useNavigate();
  const isSuper = useAppSelector(selectIsSuperAdmin);
  const [statusFilter, setStatusFilter] = useState<string>("");

  const { data, isLoading, refetch } = useGetBillingReportQuery(
    statusFilter ? { status: statusFilter } : undefined,
    { skip: !isSuper },
  );

  const rows: IBillingRow[] = useMemo(
    () => (Array.isArray(data?.data) ? (data?.data as unknown as IBillingRow[]) : []),
    [data],
  );

  const totals = useMemo(() => {
    const monthly = rows.reduce((s, r) => s + (r.monthly_total || 0), 0);
    const devices = rows.reduce((s, r) => s + (r.active_devices || 0), 0);
    return { monthly, devices };
  }, [rows]);

  if (!isSuper) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
          Billing reports are visible to Super Admins only.
        </Typography>
      </Box>
    );
  }

  if (isLoading) return <PageLoader height="20vh" />;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${alpha(GOLD, 0.18)} 0%, ${alpha(GOLD, 0.06)} 100%)`,
          color: GOLD_SOFT,
        }}>
          <PaidIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
            Device Billing
          </Typography>
          <Typography fontSize={12} color={TEXT_SECONDARY}>
            Estimated monthly invoices based on active billing-enabled devices × their per-device rate.
          </Typography>
        </Box>
        <IconButton onClick={() => refetch()}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, width: 38, height: 38, bgcolor: "#fff" }}>
          <RefreshIcon sx={{ fontSize: 18, color: NAVY }} />
        </IconButton>
      </Box>

      {/* Summary tiles */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, mb: 3 }}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
            textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Total Monthly
          </Typography>
          <Typography sx={{ fontSize: 32, fontWeight: 800, color: GOLD_SOFT, mt: 0.5 }}>
            {formatMoney(totals.monthly)}
          </Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
            textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Active Devices
          </Typography>
          <Typography sx={{ fontSize: 32, fontWeight: 800, color: NAVY, mt: 0.5 }}>
            {totals.devices}
          </Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
            textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Companies
          </Typography>
          <Typography sx={{ fontSize: 32, fontWeight: 800, color: NAVY, mt: 0.5 }}>
            {rows.length}
          </Typography>
        </Paper>
      </Box>

      {/* Filter */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, p: 2, alignItems: "center" }}>
          <TextField select size="small" label="Status filter" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ width: 220, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff" } }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="trial">Trial</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="past_due">Past Due</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  "Company", "Plan", "Status", "Trial Ends",
                  "Active Devices", "Price/Device", "Monthly Total",
                  "Billing Email", "",
                ].map((h, i) => (
                  <TableCell key={h || String(i)} align={i === 8 ? "right" : "left"}
                    sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
                      textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center", py: 6, color: TEXT_SECONDARY }}>
                    No companies match this filter.
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                        {r.name}
                      </Typography>
                      {r.max_devices !== null && (
                        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                          {r.total_devices} / {r.max_devices} devices
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY, fontWeight: 600 }}>
                    {r.subscription_plan || "—"}
                  </TableCell>
                  <TableCell><StatusPill status={r.subscription_status} /></TableCell>
                  <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY, whiteSpace: "nowrap" }}>
                    {r.trial_ends_at ? (
                      <Tooltip title={r.trial_ends_at}>
                        <span>
                          {new Date(r.trial_ends_at).toLocaleDateString()}
                          {r.trial_days_left !== null && (
                            <Typography component="span" sx={{
                              ml: 1, fontSize: 11,
                              color: r.trial_days_left < 0 ? "#b91c1c"
                                : r.trial_days_left < 3 ? "#f59e0b" : TEXT_SECONDARY,
                              fontWeight: 700,
                            }}>
                              ({r.trial_days_left}d)
                            </Typography>
                          )}
                        </span>
                      </Tooltip>
                    ) : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                    {r.active_devices}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY }}>
                    {r.avg_device_price !== null ? formatMoney(r.avg_device_price) : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 14, fontWeight: 700, color: GOLD_SOFT }}>
                    {formatMoney(r.monthly_total)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
                    {r.billing_email || "—"}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open company">
                      <IconButton size="small" onClick={() => navigate(`/company/${r.id}`)}
                        sx={{
                          border: `1px solid ${BORDER}`, borderRadius: 1.5, width: 30, height: 30,
                          color: "#3b82f6",
                          "&:hover": { bgcolor: "#dbeafe", borderColor: "#bfdbfe" },
                        }}>
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default BillingReport;
